import { useCallback, useEffect, useRef, useState } from 'react'
import {
  deleteReportView,
  downloadReportPDF,
  getReportViews,
  PeriodCategory,
  saveOrUpdateReportSchedule,
  saveOrUpdateReportView,
  SaveReportViewPayload,
} from '@services/api/report.api'
import { ReportPeriodPreset } from '@services/api/reports/finance.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { providerRoles } from '@utils/provider.util'
import { convertProviderListToDropdownOptions } from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import { ALL_METRICS_WITH_DEFAULT_SELECTION } from '@pages/Reports/CustomReportBuilder.page'
import { resolveSignedBlobUrl } from '@hooks/useSignedBlobUrl'
import { useReportMetricsSchema } from './useReportMetricsSchema'
import { useReportPayloadData } from './useReportPayloadData'
import type { ReportPayloadData } from '@services/reports/reportPayloadData'
import type { IClinic } from '@dataTypes/clinic.type'
import { logger } from '@utils/safeLogger'
import type { ReportsToolbarMenuEntry } from '@constants/Reports/reportFilterToolbarMenus.constants'

type ReportScope = 'all' | 'custom'
type ReportMetrics = Record<string, boolean>
type ReportFilters = Record<string, string>
type ReportCategories = Record<string, boolean>

type ReportMetricSchemaItem = {
  id: string
  type?: string
  label?: string
  pageTitle?: string
}

type SavedReportView = SaveReportViewPayload & {
  id: string
  clinic_report_view_id: string
  name: string
}

type ScheduleDetails = {
  every?: string
  every_details?: string
}

type ReportLocationState = {
  path?: string
  filters?: ReportFilters
  filtersBack?: ReportFilters | null
  viewId?: string
  viewIdBack?: string
  scope?: ReportScope
  metrics?: ReportMetrics
}

type UseReportManagerResult = {
  savedViewOptions: SavedReportView[]
  selectedSavedViewId: string
  savedViewsButtonLabel: string
  hasSavedViewMenu: boolean
  reportFilters: Record<string, boolean>
  reportMetrics: ReportMetrics
  reportPayloadData: ReportPayloadData
  reportPayloadLoading: boolean
  reportPayloadError: Error | null
  providerDropdownOptions: ReportsToolbarMenuEntry[]
  onSavedViewSelect: (viewId: string) => void
  updateFilters: (data: ReportFilters) => void
  updatePdfScopeAndMetrics: (data: {
    scope: ReportScope | null
    metrics: ReportMetrics | null
  }) => void
  updateCustomCategories: (data: ReportCategories | null) => void
  updateCustomTitle: (data: string | null) => void
  handleSaveView: (viewId?: string) => Promise<void>
  handleDeleteView: (viewId: string) => Promise<void>
  handleGeneratePDF: () => void
  handleScheduleReport: (
    viewId?: string,
    scheduleId?: string,
    scheduleDetails?: ScheduleDetails,
    message?: string,
  ) => Promise<void>
  handleDownloadPDF: () => Promise<void>
}

export function useReportManager(
  onFiltersDataLoaded: (
    data: ReportFilters | null,
    scope?: ReportScope,
    metrics?: ReportMetrics,
    categories?: ReportCategories,
    title?: string | null,
  ) => void,
  previewPath?: string,
  reportPageId?: string,
  loadPayloadData: boolean = false,
): UseReportManagerResult {
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const previousSelectedClinicRef = useRef<IClinic | null>(selectedClinic)
  const fetchedViewsKeyRef = useRef<string | null>(null)
  const sourceMetricsSchema = useReportMetricsSchema()
  const navigate = useNavigate()
  const previousLocationStateRef = useRef<ReportLocationState | null>(
    useLocation().state as ReportLocationState | null,
  )
  const { categoryId, reportSlug } = useParams<{
    categoryId: string
    reportSlug: string
  }>()
  const pageId =
    reportPageId ||
    (categoryId && reportSlug
      ? `/${categoryId}/${reportSlug}`
      : window.location.pathname)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [savedViews, setSavedViews] = useState<SavedReportView[]>([])
  const [selectedViewId, setSelectedViewId] = useState('')
  const [filters, setFilters] = useState<ReportFilters | null>(null)
  const [cleanedFilters, setCleanedFilters] = useState<ReportFilters | null>(
    null,
  )
  const providerList = useSelector(
    (state: RootState) =>
      state.provider.data.filter((provider) =>
        providerRoles.includes(provider.work_role ?? ''),
      ),
    shallowEqual,
  )
  const pdfScopeRef = useRef<ReportScope | null>(null)
  const [pdfScope, setPdfScope] = useState<ReportScope | null>(null)
  const pdfMetricsRef = useRef<ReportMetrics | null>(null)
  const getDefaultPdfMetricsOptions = useCallback((): Record<
    string,
    boolean
  > | null => {
    if (reportSlug) {
      const metricsOptions =
        sourceMetricsSchema
          ?.filter(
            (metric: ReportMetricSchemaItem) => metric.type === reportSlug,
          )
          ?.map((item: ReportMetricSchemaItem) => ({
            id: item.id,
            label: item.label,
          })) ?? []
      const defaultMetrics = Object.fromEntries(
        metricsOptions.map(({ id }: { id: string }) => [id, true]),
      )
      return defaultMetrics ?? null
    }
    return null
  }, [sourceMetricsSchema, reportSlug])
  const [pdfMetrics, setPdfMetrics] = useState<ReportMetrics | null>(
    (() => {
      if (pageId === '/report/custom') {
        return ALL_METRICS_WITH_DEFAULT_SELECTION
      }
      return getDefaultPdfMetricsOptions()
    })(),
  )
  const customCategoriesRef = useRef<ReportCategories | null>(null)
  const [customCategories, setCustomCategories] =
    useState<ReportCategories | null>(null)
  const customTitleRef = useRef<string | null>(null)
  const [customTitle, setCustomTitle] = useState<string | null>(null)
  const {
    data: reportPayloadData,
    loading: reportPayloadLoading,
    error: reportPayloadError,
  } = useReportPayloadData({
    enabled: loadPayloadData,
    clinicId: selectedClinic?.clinic_id,
    filters: cleanedFilters,
    metrics: pdfMetrics,
    sourceMetricsSchema,
  })

  useEffect(() => {
    if (previousLocationStateRef.current) {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      )
    }
  }, [])

  useEffect(() => {
    if (previousLocationStateRef?.current?.path) {
      previousLocationStateRef.current.filtersBack = filters
      previousLocationStateRef.current.viewIdBack = selectedViewId
    }
    if (filters) {
      const cleanedFiltersData = { ...filters }
      if (
        cleanedFiltersData['period-preset'] &&
        cleanedFiltersData['period-preset'] !== 'date-range'
      ) {
        delete cleanedFiltersData['hist-range-start']
        delete cleanedFiltersData['hist-range-end']
      }
      if (
        cleanedFiltersData['period-preset'] &&
        cleanedFiltersData['period-preset'] !== 'snapshot'
      ) {
        delete cleanedFiltersData['snapshot-as-of']
        delete cleanedFiltersData['snapshot-time']
      }
      setCleanedFilters(cleanedFiltersData)
    } else {
      setCleanedFilters(null)
    }
  }, [filters, selectedViewId])

  useEffect(() => {
    const button = document.querySelector('.reports-page-top-header__back')
    if (!button || !previousLocationStateRef.current?.path) return
    const previousLocationState = previousLocationStateRef.current

    const handleBackClick = (e: Event): void => {
      e.preventDefault()
      e.stopImmediatePropagation()
      navigate(`${previousLocationState.path}`, {
        state: {
          filters: previousLocationState.filtersBack,
          viewId: previousLocationState.viewIdBack,
          scope: pdfScopeRef.current,
          metrics: pdfMetricsRef.current,
        },
      })
    }

    button.addEventListener('click', handleBackClick, true)
    return () => {
      button.removeEventListener('click', handleBackClick, true)
    }
  }, [navigate])

  const fetchData = useCallback(
    async (requestedPageId: string): Promise<boolean> => {
      try {
        const reportViewsResponse = await getReportViews(requestedPageId)
        if (
          reportViewsResponse.statusCode === 200 &&
          'clinic_report_views' in reportViewsResponse.data
        ) {
          const savedViewsData = reportViewsResponse.data.clinic_report_views
          if (savedViewsData.length > 0) {
            setSavedViews(
              savedViewsData.map((view) => ({
                ...view,
                id: view.clinic_report_view_id,
              })),
            )
          } else {
            setSavedViews([])
          }
          if (previousLocationStateRef.current) {
            const scope = previousLocationStateRef.current?.scope
            const metrics = previousLocationStateRef.current?.metrics
            if (scope || metrics) {
              if (scope && metrics) {
                setPdfScope(scope)
                setPdfMetrics(metrics)
              }
              onFiltersDataLoaded(null, scope, metrics)
            }
            if (previousLocationStateRef.current?.viewId) {
              setSelectedViewId(previousLocationStateRef.current?.viewId)
            }
            if (previousLocationStateRef.current?.filters) {
              setFilters(previousLocationStateRef.current?.filters)
              onFiltersDataLoaded(previousLocationStateRef.current?.filters)
            }
          }
        }
        return true
      } catch (error: unknown) {
        logger.error('Error fetching report views:', error)
        return false
      }
    },
    [onFiltersDataLoaded],
  )
  useEffect(() => {
    const viewsKey = `${selectedClinic?.clinic_id ?? ''}:${pageId}`
    if (fetchedViewsKeyRef.current === viewsKey) return

    fetchedViewsKeyRef.current = viewsKey
    void fetchData(pageId).then((loaded) => {
      if (!loaded && fetchedViewsKeyRef.current === viewsKey) {
        fetchedViewsKeyRef.current = null
      }
    })
  }, [pageId, fetchData, selectedClinic?.clinic_id])

  useEffect(() => {
    if (previousSelectedClinicRef.current !== selectedClinic) {
      const resetProviderFilter = {
        ...filters,
        'all-providers': 'all-providers',
      }
      setFilters(resetProviderFilter)
      onFiltersDataLoaded(resetProviderFilter)
      setSelectedViewId('')
      previousSelectedClinicRef.current = selectedClinic
      previousLocationStateRef.current = null
    }
  }, [filters, onFiltersDataLoaded, selectedClinic])

  const selectSavedView = (viewId: string): void => {
    setSelectedViewId(viewId)
    const selectedView = savedViews.find((view) => view.id === viewId)
    const selectedFilters: ReportFilters = {
      'period-category': selectedView?.period_category ?? '',
      'period-preset': selectedView?.period_preset ?? '',
      'provider-filter': selectedView?.provider_filter ?? '',
      ...(selectedView?.hist_range_start
        ? {
            'hist-range-start': selectedView.hist_range_start,
          }
        : {}),
      ...(selectedView?.hist_range_end
        ? {
            'hist-range-end': selectedView.hist_range_end,
          }
        : {}),
      ...(selectedView?.snapshot_as_of
        ? {
            'snapshot-as-of': selectedView.snapshot_as_of,
          }
        : {}),
      ...(selectedView?.snapshot_time
        ? {
            'snapshot-time': selectedView.snapshot_time,
          }
        : {}),
    }
    const scope = selectedView?.scope
    const metrics = selectedView?.metrics
    const categories = selectedView?.custom_report_categories
    const title = selectedView?.custom_report_title
    if (scope || metrics || categories || title) {
      if (scope && metrics) {
        setPdfScope(scope)
        setPdfMetrics(metrics)
      } else {
        setPdfScope(null)
        setPdfMetrics(null)
      }
      if (categories) {
        setCustomCategories(categories)
      }
      if (title) {
        setCustomTitle(title)
      }
      setFilters(selectedFilters)
      onFiltersDataLoaded(
        selectedFilters,
        scope,
        metrics ?? undefined,
        categories,
        title,
      )
    } else {
      setPdfScope(null)
      setPdfMetrics(null)
      setCustomCategories(null)
      setCustomTitle(null)
      setFilters(selectedFilters)
      onFiltersDataLoaded(selectedFilters)
    }
  }

  const validateRequiredFields = (): boolean => {
    if (pageId === '/report/custom') {
      if (!customTitleRef.current?.trim()) {
        showToast(
          createFailedToast(
            'Report title is required',
            'Please enter a report title before saving this view.',
          ),
        )
        return false
      }
    }
    return true
  }

  const handleSaveView = async (viewId?: string): Promise<void> => {
    if (!validateRequiredFields()) return
    if (!filters) return
    const activeFilters = filters
    const customReportTitle = customTitleRef.current?.trim() || null
    showLoading({ container: 'report-page' })
    try {
      const payload: SaveReportViewPayload = {
        ...(viewId ? { clinic_report_view_id: viewId } : {}),
        page_id: pageId,
        ...(categoryId ? { category: categoryId } : {}),
        period_category: activeFilters['period-category'] as PeriodCategory,
        period_preset: activeFilters['period-preset'] as ReportPeriodPreset,
        provider_filter: activeFilters['provider-filter'],
        ...(activeFilters['period-category'] === 'historical' &&
        activeFilters['period-preset'] === 'date-range'
          ? {
              hist_range_start: activeFilters['hist-range-start'],
              hist_range_end: activeFilters['hist-range-end'],
            }
          : {}),
        ...(activeFilters['period-category'] === 'snapshot' &&
        activeFilters['period-preset'] === 'snapshot'
          ? {
              snapshot_as_of: activeFilters['snapshot-as-of'],
              snapshot_time: activeFilters['snapshot-time'],
            }
          : {}),
        ...(pdfScopeRef.current
          ? { scope: pdfScopeRef.current }
          : { scope: 'all' }),
        ...(pdfMetricsRef.current &&
        Object.keys(pdfMetricsRef.current).length > 0
          ? { metrics: pdfMetricsRef.current }
          : { metrics: getDefaultPdfMetricsOptions() }),
        ...(pageId === '/report/custom'
          ? {
              is_custom_report: true,
              ...(customReportTitle ? { name: customReportTitle } : {}),
            }
          : {}),
        ...(customReportTitle
          ? {
              custom_report_title: customReportTitle,
            }
          : { custom_report_title: null }),
        ...(customCategoriesRef.current
          ? {
              custom_report_categories: customCategoriesRef.current,
            }
          : {}),
      }
      const reportViewsResponse = await saveOrUpdateReportView(payload)
      if (
        reportViewsResponse.statusCode === 200 &&
        'clinic_report_view' in reportViewsResponse.data
      ) {
        const savedViewsData = reportViewsResponse.data.clinic_report_view
        if (!viewId) {
          setSavedViews((prevSavedViews) => [
            ...prevSavedViews,
            {
              ...savedViewsData,
              id: savedViewsData.clinic_report_view_id,
            },
          ])
          setSelectedViewId(savedViewsData.clinic_report_view_id)
          const selectedFilters: ReportFilters = {
            'period-category': savedViewsData.period_category ?? '',
            'period-preset': savedViewsData.period_preset ?? '',
            'provider-filter': savedViewsData.provider_filter ?? '',
            ...(savedViewsData.hist_range_start
              ? {
                  'hist-range-start': savedViewsData.hist_range_start,
                }
              : {}),
            ...(savedViewsData.hist_range_end
              ? {
                  'hist-range-end': savedViewsData.hist_range_end,
                }
              : {}),
            ...(savedViewsData.snapshot_as_of
              ? {
                  'snapshot-as-of': savedViewsData.snapshot_as_of,
                }
              : {}),
            ...(savedViewsData.snapshot_time
              ? {
                  'snapshot-time': savedViewsData.snapshot_time,
                }
              : {}),
          }
          setFilters(selectedFilters)
          onFiltersDataLoaded(selectedFilters)
          setPdfMetrics(savedViewsData.metrics ?? null)
        } else {
          setSavedViews((prevSavedViews) =>
            prevSavedViews.map((view) =>
              view.id === savedViewsData.clinic_report_view_id
                ? {
                    ...view,
                    ...savedViewsData,
                  }
                : view,
            ),
          )
        }
        hideLoading('report-page')
        showToast(
          createSuccessToast(
            viewId ? 'View updated successfully' : 'View saved successfully',
            viewId
              ? 'View has been updated successfully.'
              : 'View has been saved successfully.',
          ),
        )
      } else {
        hideLoading('report-page')
        showToast(
          createFailedToast(
            viewId ? 'View updating failed' : 'View saving failed',
            viewId
              ? 'View updating failed. Please try again later.'
              : 'View saving failed. Please try again later.',
          ),
        )
      }
    } catch (error: unknown) {
      logger.error('Error saving report view:', error)
      hideLoading('report-page')
      showToast(
        createFailedToast(
          viewId ? 'View updating failed' : 'View saving failed',
          viewId
            ? 'View updating failed. Please try again later.'
            : 'View saving failed. Please try again later.',
        ),
      )
    }
  }

  const handleDeleteView = async (viewId: string): Promise<void> => {
    showLoading({ container: 'report-page' })
    try {
      const reportViewsResponse = await deleteReportView(viewId)
      if (reportViewsResponse.statusCode === 200) {
        setSavedViews((prevSavedViews) =>
          prevSavedViews.filter((view) => view.id !== viewId),
        )
        const currentMetrics = pdfMetricsRef.current
        const currentPath = previousLocationStateRef.current?.path
        previousLocationStateRef.current = null
        if (currentPath) {
          previousLocationStateRef.current = {
            path: currentPath,
          }
        }
        setPdfScope(null)
        setPdfMetrics(null)
        setSelectedViewId('')
        const defaultFilters = {
          'period-category': 'latest-data',
          'period-preset': 'this-month',
          'provider-filter': 'all-providers',
          'hist-range-start': '',
          'hist-range-end': '',
          'snapshot-as-of': '',
          'snapshot-time': '',
        }
        setFilters(defaultFilters)
        if (pageId === '/report/custom') {
          const defaultCustomCategories = {
            finance: false,
            patients: true,
            engagement: false,
            operations: false,
            'provider-performance': false,
          }
          onFiltersDataLoaded(
            defaultFilters,
            'custom',
            ALL_METRICS_WITH_DEFAULT_SELECTION,
            defaultCustomCategories,
            null,
          )
        } else {
          onFiltersDataLoaded(
            defaultFilters,
            'all',
            currentMetrics
              ? Object.fromEntries(
                  Object.keys(currentMetrics).map((key) => [key, true]),
                )
              : undefined,
          )
        }
        hideLoading('report-page')
        showToast(
          createSuccessToast(
            'View deleted successfully',
            'View has been deleted successfully.',
          ),
        )
      } else {
        hideLoading('report-page')
        showToast(
          createFailedToast(
            'View deleting failed',
            'View deleting failed. Please try again later.',
          ),
        )
      }
    } catch (error: unknown) {
      logger.error('Error deleting report view:', error)
      hideLoading('report-page')
      showToast(
        createFailedToast(
          'View deleting failed',
          'View deleting failed. Please try again later.',
        ),
      )
    }
  }

  const handleGeneratePDF = (): void => {
    navigate(`${previewPath || '/preview'}`, {
      state: {
        path: location.pathname,
        filters: filters,
        viewId: selectedViewId,
        scope: pdfScopeRef.current,
        metrics: pdfMetricsRef.current,
      },
    })
  }

  const handleScheduleReport = async (
    viewId?: string,
    scheduleId?: string,
    scheduleDetails?: ScheduleDetails,
    message?: string,
  ): Promise<void> => {
    if (!validateRequiredFields()) return
    if (!filters) return
    const activeFilters = filters
    showLoading({ container: 'report-page' })
    try {
      const reportScheduleResponse = await saveOrUpdateReportSchedule({
        ...(scheduleDetails?.every ? { every: scheduleDetails.every } : {}),
        ...(scheduleDetails?.every_details
          ? { every_details: scheduleDetails.every_details }
          : {}),
        ...(viewId ? { clinic_report_view_id: viewId } : {}),
        page_id: pageId,
        ...(categoryId ? { category: categoryId } : {}),
        period_category: activeFilters['period-category'] as PeriodCategory,
        period_preset: activeFilters['period-preset'] as ReportPeriodPreset,
        provider_filter: activeFilters['provider-filter'],
        ...(activeFilters['period-category'] === 'historical' &&
        activeFilters['period-preset'] === 'date-range'
          ? {
              hist_range_start: activeFilters['hist-range-start'],
              hist_range_end: activeFilters['hist-range-end'],
            }
          : {}),
        ...(activeFilters['period-category'] === 'snapshot' &&
        activeFilters['period-preset'] === 'snapshot'
          ? {
              snapshot_as_of: activeFilters['snapshot-as-of'],
              snapshot_time: activeFilters['snapshot-time'],
            }
          : {}),
        ...(pdfScopeRef.current
          ? { scope: pdfScopeRef.current }
          : { scope: 'all' }),
        ...(pdfMetricsRef.current
          ? { metrics: pdfMetricsRef.current }
          : { metrics: getDefaultPdfMetricsOptions() }),
        ...(pageId === '/report/custom'
          ? {
              is_custom_report: true,
            }
          : {}),
        ...(customTitleRef.current
          ? {
              custom_report_title: customTitleRef.current,
            }
          : { custom_report_title: null }),
        ...(customCategoriesRef.current
          ? {
              custom_report_categories: customCategoriesRef.current,
            }
          : {}),
      })

      if (
        reportScheduleResponse.statusCode === 200 &&
        'clinic_report_schedule' in reportScheduleResponse.data
      ) {
        hideLoading('report-page')
        showToast(
          createSuccessToast(
            scheduleId
              ? 'Schedule report updated successfully'
              : 'Schedule report saved successfully',
            message ||
              (scheduleId
                ? 'Schedule report has been updated successfully.'
                : 'Schedule report has been saved successfully.'),
          ),
        )
      } else {
        hideLoading('report-page')
        showToast(
          createFailedToast(
            scheduleId
              ? 'Schedule report updating failed'
              : 'Schedule report saving failed',
            scheduleId
              ? 'Schedule report updating failed. Please try again later.'
              : 'Schedule report saving failed. Please try again later.',
          ),
        )
      }
    } catch (error: unknown) {
      logger.error('Error saving report schedule:', error)
      hideLoading('report-page')
      showToast(
        createFailedToast(
          scheduleId
            ? 'Schedule report updating failed'
            : 'Schedule report saving failed',
          scheduleId
            ? 'Schedule report updating failed. Please try again later.'
            : 'Schedule report saving failed. Please try again later.',
        ),
      )
    }
  }

  function base64ToBlob(base64: string, contentType: string): Blob {
    const byteChars = atob(base64)
    const bytes = new Uint8Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i++) {
      bytes[i] = byteChars.charCodeAt(i)
    }
    return new Blob([bytes], { type: contentType })
  }

  const handleDownloadPDF = async (): Promise<void> => {
    showLoading({ container: 'report-page' })
    try {
      if (reportPayloadLoading) {
        throw new Error('Report data is still loading.')
      }
      if (reportPayloadError) throw reportPayloadError
      const clinicLogoUrl = await resolveSignedBlobUrl(
        selectedClinic?.logo_url,
        '',
      )
      const reportPDFResponse = await downloadReportPDF({
        selectedClinic: {
          ...selectedClinic,
          ...(clinicLogoUrl ? { logo_url: clinicLogoUrl } : {}),
        },
        reportTitle:
          sourceMetricsSchema.find((metric) => metric.type === reportSlug)
            ?.pageTitle || customTitleRef.current,
        reportFiltersAndMetrics: {
          filters: cleanedFilters,
          metrics: pdfMetrics,
        },
        generatedOnDisplay: new Date().toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }),
        headerSwitch: false,
        isCustomReport: pageId === '/report/custom',
        payloadData: reportPayloadData,
      })

      if (reportPDFResponse.statusCode === 200) {
        const json = await reportPDFResponse
        const { pdfBase64, filename } = json.data // adjust to your envelope's shape
        const blob = base64ToBlob(pdfBase64, 'application/pdf')
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename ?? `${'sample'}-report.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
        hideLoading('report-page')
        showToast(
          createSuccessToast(
            'Report PDF downloaded successfully',
            'Report PDF has been downloaded successfully.',
          ),
        )
      } else {
        hideLoading('report-page')
        showToast(
          createFailedToast(
            'Report PDF downloading failed',
            'Report PDF downloading failed. Please try again later.',
          ),
        )
      }
    } catch (error: unknown) {
      logger.error('Error downloading report PDF:', error)
      hideLoading('report-page')
      showToast(
        createFailedToast(
          'Report PDF downloading failed',
          'Report PDF downloading failed. Please try again later.',
        ),
      )
    }
  }

  // Keep refs in sync for event handlers / navigation that need latest values
  useEffect(() => {
    pdfScopeRef.current = pdfScope
  }, [pdfScope])

  useEffect(() => {
    pdfMetricsRef.current = pdfMetrics
  }, [pdfMetrics])

  useEffect(() => {
    customCategoriesRef.current = customCategories
  }, [customCategories])

  useEffect(() => {
    customTitleRef.current = customTitle
  }, [customTitle])

  return {
    savedViewOptions: savedViews,
    selectedSavedViewId: selectedViewId,
    savedViewsButtonLabel:
      savedViews.find((view) => view.id === selectedViewId)?.name ||
      'Select a saved view',
    hasSavedViewMenu: savedViews.length > 0,
    reportFilters: (cleanedFilters ?? {}) as unknown as Record<string, boolean>,
    reportMetrics: pdfMetrics ?? {},
    reportPayloadData,
    reportPayloadLoading,
    reportPayloadError,
    providerDropdownOptions: convertProviderListToDropdownOptions(providerList),
    onSavedViewSelect: selectSavedView,
    updateFilters: (data: ReportFilters) => {
      setFilters(data)
    },
    updatePdfScopeAndMetrics: (data: {
      scope: ReportScope | null
      metrics: ReportMetrics | null
    }) => {
      setPdfScope(data.scope)
      setPdfMetrics(data.metrics)
    },
    updateCustomCategories: (data: ReportCategories | null) => {
      setCustomCategories(data)
    },
    updateCustomTitle: (data: string | null) => {
      setCustomTitle(data)
    },
    handleSaveView,
    handleDeleteView,
    handleGeneratePDF,
    handleScheduleReport,
    handleDownloadPDF,
  }
}
